import { createColumnHelper } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";

import i18n from "@/i18n";
import { MODAL_ROUTES } from "@/router";
import type { useNavigateModal } from "@/router/useNavigateModal";
import {
  mapBackendStatusToDisplayStatus,
  mapDisplayStatusToColor,
} from "@/types";
import type { Status, UserBundleAccess } from "@/types";
import { Button } from "@/ui";
import { Badge } from "@/ui/Badge";
import { mapCourseTypeLabel } from "@/utils/courseUtils";
import { formatDateToDDMMYYYY } from "@/utils/dates";

const columnHelper = createColumnHelper<UserBundleAccess>();

export function getTableColumns(
  approvedTab: boolean,
  navigateModal: ReturnType<typeof useNavigateModal>,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<UserBundleAccess, any>[] = [
    columnHelper.display({
      id: "user",
      header: i18n.t("materials.user_name"),
      cell: (info) => {
        const { first_name, last_name, email } = info.row.original.user;
        return (
          <div className="flex flex-col">
            <span className="font-semibold text-neutral-900">
              {first_name} {last_name}
            </span>
            <span className="text-xs text-neutral-500">{email}</span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "bundle",
      header: i18n.t("materials.course_name"),
      cell: (info) => {
        const bundle = info.row.original.bundle;
        return (
          <div className="flex flex-col">
            <span className="font-medium text-neutral-900">{bundle?.name}</span>
            {bundle?.type ? (
              <span className="text-xs text-neutral-500">
                {mapCourseTypeLabel(bundle.type as string)}
              </span>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor(!approvedTab ? "created_at" : "approved_on", {
      header: i18n.t(
        !approvedTab ? "materials.date_requested" : "materials.date_approved",
      ),
      cell: (info) => (
        <span className="text-sm text-neutral-600">
          {formatDateToDDMMYYYY(info.getValue() as Date | null)}
        </span>
      ),
    }),
    columnHelper.accessor("status", {
      header: i18n.t("materials.status"),
      cell: (info) => (
        <Badge
          text={mapBackendStatusToDisplayStatus(info.getValue() as Status)}
          color={mapDisplayStatusToColor(info.getValue() as Status)}
        />
      ),
    }),
  ];

  if (approvedTab) {
    columns.push(
      columnHelper.display({
        id: "accessPeriod",
        header: "Access period",
        cell: (info) => {
          const start = info.row.original.access_start_date;
          const end = info.row.original.access_end_date;
          return (
            <span className="whitespace-nowrap text-sm text-neutral-600">
              {start ? formatDateToDDMMYYYY(start) : "—"}
              {" → "}
              {end ? (
                formatDateToDDMMYYYY(end)
              ) : (
                <span className="italic text-neutral-400">
                  {i18n.t("materials.indefinite")}
                </span>
              )}
            </span>
          );
        },
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: "manageAccess",
      header: "",
      cell: (info) => {
        const userBundleAccess = info.row.original;
        const isWithdrawn = userBundleAccess.status === "withdrawn";
        return (
          <Button
            onClick={() =>
              navigateModal(MODAL_ROUTES.manageUserBundleAccess, {
                userBundleAccess,
              })
            }
            variant="outlined"
            size="sm"
            disabled={isWithdrawn}
          >
            {i18n.t("materials.manage_access")}
          </Button>
        );
      },
    }),
  );

  return columns;
}
